/**
 * PublishingOverviewSection — a *preview* of a typical publishing week.
 *
 * NOT the authoritative schedule (Sprint 6 will own that). We label it
 * explicitly as a "Preview" so the owner knows this is a shape hint,
 * not a promise of exact posting times.
 */
import { Card } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { CHANNEL_LABEL } from "@/features/marketing-strategy/types";
import type { PublishingOverview } from "../../types";

export function PublishingOverviewSection({
  overview,
}: {
  overview: PublishingOverview;
}): JSX.Element {
  return (
    <section data-testid="room-publishing-overview">
      <div className="mb-6 flex flex-wrap items-baseline gap-3">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">
          Publishing Overview
        </p>
        <Badge variant="secondary" data-testid="room-publishing-preview-badge">
          Preview
        </Badge>
      </div>
      <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-6">
        A typical week
      </h2>

      {!overview.available ? (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">{overview.note}</p>
        </Card>
      ) : (
        <>
          <Card className="p-6">
            <ul
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
              data-testid="room-publishing-days"
            >
              {overview.days.map((d) => (
                <li
                  key={d.day}
                  className="rounded-md border border-border p-4"
                  data-testid={`room-publishing-day-${d.day.toLowerCase()}`}
                >
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                    {d.day}
                  </p>
                  <p className="font-medium">
                    {CHANNEL_LABEL[d.channel_key] ?? d.channel_key}
                  </p>
                </li>
              ))}
            </ul>
          </Card>
          <p className="mt-3 text-sm text-muted-foreground italic">{overview.note}</p>
        </>
      )}
    </section>
  );
}
